import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Card,
  CardContent,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import {
  GET_FORM,
  GET_FORM_SUBMISSION,
  UPDATE_FORM_SUBMISSION,
  Form,
  FormField,
  FieldType,
  FormSection,
} from "../../graphql/operations";
import { Helmet } from "react-helmet-async";

const EditFormSubmission: React.FC = () => {
  const navigate = useNavigate();
  const { submissionId } = useParams<{ submissionId: string }>();
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [submissionStatus, setSubmissionStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");

  // Fetch existing submission data
  const {
    data: submissionData,
    loading: submissionLoading,
    error: submissionError,
  } = useQuery(GET_FORM_SUBMISSION, {
    variables: { submissionId },
    skip: !submissionId,
  });

  // Fetch form structure
  let submission: any = null;
  const items = submissionData?.getFormSubmission?.items || [];
  if (items.length > 0) {
    submission = items[0];
  }

  const formId = submission?.formId;
  const {
    data: formDataResponse,
    loading: formLoading,
    error: formError,
  } = useQuery(GET_FORM, {
    variables: { id: formId },
    skip: !formId,
  });

  const [updateFormSubmission, { loading: updating, error: updateError }] =
    useMutation(UPDATE_FORM_SUBMISSION);

  const form: Form | null = formDataResponse?.getForm || null;
  useEffect(() => {
    if (submission && form) {
      // Initialize form data with existing submission values
      const initialData: Record<string, any> = {};

      // Convert submission data array to object for easier access
      const submissionDataMap: Record<string, any> = {};
      submission.data.forEach((field: any) => {
        submissionDataMap[field.label] = field.value;
      });

      // Get all fields from all sections and populate with existing values
      const allFields: FormField[] = [];
      form.sections?.forEach((section: FormSection) => {
        allFields.push(...(section.fields || []));
      });

      allFields.forEach((field: FormField) => {
        initialData[field.label] =
          submissionDataMap[field.label] ||
          (field.type === FieldType.BOOLEAN ? false : "");
      });

      setFormData(initialData);
    }
  }, [submission, form]);

  const handleFieldChange = (fieldLabel: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldLabel]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!submissionId || !form) return;

    setSubmissionStatus("submitting");

    try {
      // Prepare submission data in the required format
      const submissionInput = Object.entries(formData).map(
        ([label, value]) => ({
          label,
          value: value.toString(),
        })
      );

      await updateFormSubmission({
        variables: {
          formId,
          submissionId,
          input: submissionInput,
        },
      });

      setSubmissionStatus("success");
      setTimeout(() => {
        navigate("/dashboard/submissions");
      }, 2000);
    } catch (err) {
      setSubmissionStatus("error");
      console.error("Error updating form submission:", err);
    }
  };

  const renderField = (field: FormField) => {
    const value = formData[field.label] || "";

    switch (field.type) {
      case FieldType.TEXT:
      case FieldType.EMAIL:
        return (
          <TextField
            key={field.label}
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            required={field.required}
            fullWidth
            type={field.type === FieldType.EMAIL ? "email" : "text"}
            sx={{ mb: 2 }}
          />
        );

      case FieldType.NUMBER:
        return (
          <TextField
            key={field.label}
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            required={field.required}
            fullWidth
            type="number"
            sx={{ mb: 2 }}
          />
        );

      case FieldType.SELECT:
        return (
          <FormControl key={field.label} fullWidth sx={{ mb: 2 }}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              label={field.label}
              onChange={(e) => handleFieldChange(field.label, e.target.value)}
              required={field.required}
            >
              {field.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case FieldType.BOOLEAN:
        return (
          <FormControlLabel
            key={field.label}
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) =>
                  handleFieldChange(field.label, e.target.checked)
                }
                required={field.required}
              />
            }
            label={field.label}
            sx={{ mb: 2 }}
          />
        );

      case FieldType.DATE:
        return (
          <TextField
            key={field.label}
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            required={field.required}
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
        );

      default:
        return (
          <TextField
            key={field.label}
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            required={field.required}
            fullWidth
            sx={{ mb: 2 }}
          />
        );
    }
  };

  if (submissionLoading || formLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (submissionError || formError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading {submissionError ? "submission" : "form"}:{" "}
        {(submissionError || formError)?.message}
      </Alert>
    );
  }

  if (!form || !submission) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {!form ? "Form" : "Submission"} not found.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, margin: "0 auto", p: 3 }}>
      <Helmet>
        <title>Almond Tree Dashboard - Edit {form.title}</title>
      </Helmet>

      <Button
        variant="outlined"
        onClick={() => navigate("/dashboard/submissions")}
        sx={{ mb: 3 }}
      >
        ← Back to Submissions
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Edit {form.title}
        </Typography>

        {form.description && (
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            {form.description}
          </Typography>
        )}

        <Typography
          variant="caption"
          color="textSecondary"
          display="block"
          sx={{ mb: 2 }}
        >
          Form Version: {form.version} • {form.fields.length} fields
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Originally submitted by: {submission.submittedBy} on{" "}
          {new Date(submission.submittedAt).toLocaleDateString()}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {form.fields.map((field) => (
              <Card key={field.label} variant="outlined">
                <CardContent>
                  <Typography
                    sx={{ fontSize: 14 }}
                    color="text.secondary"
                    gutterBottom
                  >
                    {field.label}
                  </Typography>
                  {renderField(field)}
                  {field.required && (
                    <Typography variant="caption" color="textSecondary">
                      * Required field
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}

            {(updateError || submissionStatus === "error") && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Error updating form:{" "}
                {updateError?.message ||
                  "Please check your inputs and try again."}
              </Alert>
            )}

            {submissionStatus === "success" && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Form updated successfully! Redirecting to submissions list...
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={updating || submissionStatus === "success"}
              sx={{ mt: 3 }}
            >
              {updating ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Updating...
                </>
              ) : (
                "Update Form"
              )}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default EditFormSubmission;
